/* /ess/attendance — own attendance month (biometric + mobile punches) and check in/out. */
import { useState } from "react";
import { useAttendanceMonth, useToday } from "../../data/hooks";
import type { DailyAttendance, DayStatus } from "../../data/types";
import { minToHm } from "../../domain/dates";
import { useI18n } from "../../i18n";
import { periodLabel } from "../../lib/format";
import { Card, Chip, DualDate, EmptyState, ErrorBox, Kpi, PageHeader, Select, Skeleton } from "../../ui";
import { CheckButton } from "./CheckButton";
import { NoLink } from "./EssHome";
import { periodOf, shiftPeriod, useEmployeeId } from "./essLib";
import { otPct } from "../../lib/country";

export function monthSummary(rows: DailyAttendance[]) {
  const c = (s: DayStatus) => rows.filter((r) => r.status === s).length;
  return {
    present: c("Present") + c("Late"),
    late: c("Late"),
    absent: c("Absent"),
    leave: c("On leave"),
    missing: c("Missing punch"),
    workedMinutes: rows.reduce((a, r) => a + r.workedMinutes, 0),
    overtimeMinutes: rows.reduce((a, r) => a + r.overtimeMinutes, 0),
    lateMinutes: rows.reduce((a, r) => a + r.lateMinutes, 0),
  };
}

const STATUS_AR: Record<string, string> = { Present: "حاضر", Late: "متأخر", Absent: "غائب", "On leave": "في إجازة", Weekend: "عطلة", "Missing punch": "بصمة ناقصة", "Not in yet": "لم يحضر بعد" };

function Body({ empId }: { empId: number }) {
  const { L, t, lang } = useI18n();
  const today = useToday();
  const cur = periodOf(today);
  const [sel, setSel] = useState<string | null>(null);
  const period = sel ?? cur;
  const q = useAttendanceMonth(empId, period);
  const rows = (q.data || []).filter((r) => r.employeeId === empId).slice().reverse();
  const s = monthSummary(rows);
  const periods = [0, 1, 2, 3, 4, 5].map((k) => shiftPeriod(cur, -k));
  return (
    <div className="ess" data-testid="ess-attendance">
      <PageHeader title={L("My attendance", "حضوري")} sub={L("From the office fingerprint/face devices and mobile check-ins", "من أجهزة البصمة في المكتب وتسجيلات الجوال")}
        actions={<Select aria-label={t("Month")} value={period} onChange={(e) => setSel(e.target.value)} options={periods.map((p) => [p, periodLabel(p, lang)] as [string, string])} data-testid="att-period" />} />
      {period === cur && (
        <Card title={L("Today", "اليوم")} className="mb">
          <div className="row between">
            <div className="sm muted"><DualDate date={today} inline /></div>
            <CheckButton employeeId={empId} today={today} variant="buttons" />
          </div>
        </Card>
      )}
      {q.error ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : q.isLoading ? <Skeleton /> : (
        <>
          <div className="g g-4 mb">
            <Kpi label={L("Days present", "أيام الحضور")} value={<span data-testid="att-present">{s.present}</span>} icon="check" foot={L(s.late + " late", s.late + " متأخر")} />
            <Kpi label={L("Absent", "الغياب")} value={<span data-testid="att-absent">{s.absent}</span>} icon="x" tone={s.absent ? "bad" : ""} foot={s.missing ? L(s.missing + " missing punch", s.missing + " بصمة ناقصة") : L("No missing punches", "لا توجد بصمات ناقصة")} />
            <Kpi label={L("Hours worked", "ساعات العمل")} value={<span data-testid="att-hours">{(s.workedMinutes / 60).toFixed(1)}</span>} unit={L("h", "س")} icon="clock" tone="info" />
            <Kpi label={L("Overtime", "العمل الإضافي")} value={<span data-testid="att-ot">{(s.overtimeMinutes / 60).toFixed(1)}</span>} unit={L("h", "س")} icon="zap" tone="gold" foot={L("Paid at " + otPct() + " in payroll", "يُحتسب " + otPct() + " في الرواتب")} />
          </div>
          <Card flush>
            {rows.length === 0 ? <EmptyState title={L("No attendance for this month yet", "لا يوجد حضور لهذا الشهر بعد")} desc={L("Punches from the office devices appear here within seconds.", "تظهر البصمات من أجهزة المكتب هنا خلال ثوانٍ.")} /> : (
              <div className="tw">
                <table className="tbl cards" data-testid="att-table">
                  <thead><tr><th>{L("Date", "التاريخ")}</th><th>{L("In", "دخول")}</th><th>{L("Out", "خروج")}</th><th className="end">{L("Worked", "العمل")}</th><th className="end hide-md">{L("Late", "تأخير")}</th><th>{t("Status")}</th><th className="hide-md">{L("Source", "المصدر")}</th></tr></thead>
                  <tbody>{rows.map((r) => (
                    <tr key={r.date} data-testid={"att-" + r.date}>
                      <td className="k1"><DualDate date={r.date} /></td>
                      <td className="x num" data-l={L("In", "دخول")}>{r.firstIn ?? "—"}</td>
                      <td className="x num" data-l={L("Out", "خروج")}>{r.lastOut ?? "—"}</td>
                      <td className="end k3 num" data-l={L("Worked", "العمل")}>{r.workedMinutes ? minToHm(r.workedMinutes) : "—"}</td>
                      <td className="end hide-md num">{r.lateMinutes ? r.lateMinutes + L(" min", " د") : "—"}</td>
                      <td className="k2"><Chip status={r.status}>{L(r.status, STATUS_AR[r.status] ?? r.status)}</Chip></td>
                      <td className="hide-md xs muted">{r.source || "—"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export default function EssAttendance() {
  const id = useEmployeeId();
  return id == null ? <NoLink /> : <Body empId={id} />;
}
